import { useState, useEffect } from 'react';
import { Globe, X } from 'lucide-react';
import { changeLanguage, getCurrentLanguage, type SupportedLanguage } from '@/i18n';

const bannerMessages: Record<string, { text: string; btn: string; code: SupportedLanguage }> = {
  en: { text: 'This site is available in English.', btn: 'Switch to EN', code: 'en-US' },
  es: { text: 'Este sitio está disponible en español.', btn: 'Cambiar a ES', code: 'es-ES' },
  fr: { text: 'Ce site est disponible en français.', btn: 'Passer en FR', code: 'fr-FR' },
};

const LanguageBanner = () => {
  const [visible, setVisible] = useState(false);
  const [bannerLang, setBannerLang] = useState<string | null>(null);

  useEffect(() => {
    const currentLang = getCurrentLanguage();
    const browserLang = navigator.language.slice(0, 2);
    const bannerSeen = sessionStorage.getItem('bs_lang_banner');

    if (!currentLang.startsWith('pt') || !bannerMessages[browserLang] || bannerSeen) return;

    setBannerLang(browserLang);
    setVisible(true);
  }, []);

  const dismiss = () => {
    setVisible(false);
    sessionStorage.setItem('bs_lang_banner', 'true');
  };

  const switchLang = async () => {
    if (!bannerLang || !bannerMessages[bannerLang]) return;
    await changeLanguage(bannerMessages[bannerLang].code);
    dismiss();
  };

  if (!visible || !bannerLang) return null;
  const msg = bannerMessages[bannerLang];

  return (
    <div
      className="fixed top-16 left-0 right-0 z-[999] border-b border-border/10 py-3 px-6 flex items-center justify-center gap-5 animate-in slide-in-from-top duration-350"
      style={{ background: 'rgba(12,12,12,0.97)' }}
    >
      <Globe className="w-4 h-4 text-muted-foreground/60 shrink-0" strokeWidth={1.2} />
      <span className="text-xs tracking-[0.07em] text-muted-foreground">{msg.text}</span>
      <button
        onClick={switchLang}
        className="text-xs tracking-[0.07em] text-foreground underline underline-offset-[3px] bg-transparent border-none cursor-pointer"
      >
        {msg.btn} →
      </button>
      <button
        onClick={dismiss}
        className="absolute right-4 bg-transparent border-none text-muted-foreground/30 hover:text-muted-foreground/70 transition-colors cursor-pointer"
        aria-label="Close"
      >
        <X className="w-4 h-4" strokeWidth={1.5} />
      </button>
    </div>
  );
};

export default LanguageBanner;
